/**
 * QuickCompare cost-breakdown builder.
 * ─────────────────────────────────────────────────────────────────────────────
 * QuickCompare has its own self-contained finance engine (pmt / vehVal /
 * runningCost in QuickCompare.jsx) with a flat inputs shape that is NOT
 * compatible with the main App's `buildCostComparison`. So it gets its own
 * builder here.
 *
 * Each QuickCompare result carries an unrounded `_components` object (attached
 * in calcResults, where every value is already in scope — no maths change).
 * This builder maps those components into the shared LineItem contract
 * ({ key, label, value, sign, working }) used by ProgressiveCard. Because the
 * components are unrounded, sum(items) reconciles to the rounded `totalCost`
 * within $1.
 *
 * ctx = { exitValue, runPerYear, holdYears, stampDuty } from the calcResults
 * return — shared across methods (running/exit don't depend on finance).
 * ─────────────────────────────────────────────────────────────────────────
 */

const fmt = (n) =>
  n == null
    ? '–'
    : (n < 0 ? '-$' : '$') +
      Math.abs(Math.round(n)).toLocaleString('en-AU');

/** Sum item magnitudes by sign, excluding the terminal 'total' row. */
export function signedSum(items) {
  return items
    .filter((it) => it.key !== 'total')
    .reduce((s, it) => s + (it.sign === '+' ? it.value : -it.value), 0);
}

/** True iff items reconcile to the 'total' row within epsilon. */
export function verify(items, epsilon = 1) {
  const total = items.find((it) => it.key === 'total');
  if (!total) return false;
  return Math.abs(signedSum(items) - total.value) < epsilon;
}

function totalItem(result) {
  return {
    key: 'total',
    label: 'True total cost',
    value: result.totalCost,
    sign: '+',
    working: fmt(result.totalCost),
  };
}

function itemsCash(c, ctx) {
  return [
    {
      key: 'upfront',
      label: 'Upfront cost (drive-away)',
      value: c.upfront,
      sign: '+',
      working: `${fmt(c.upfront)} paid up front`,
    },
    {
      key: 'running',
      label: 'Running costs',
      value: c.running,
      sign: '+',
      working: `${fmt(ctx.runPerYear)} / yr × ${ctx.holdYears} yrs = ${fmt(c.running)}`,
    },
    {
      key: 'opportunityCost',
      label: 'Opportunity cost',
      value: c.opportunityCost,
      sign: '+',
      working: `interest forgone on ${fmt(c.upfront)} over ${ctx.holdYears} yrs = ${fmt(c.opportunityCost)}`,
    },
    {
      key: 'exitValue',
      label: 'Exit value (sale price)',
      value: c.exitValue,
      sign: '-',
      working: `resale at year ${ctx.holdYears} = ${fmt(c.exitValue)}`,
    },
  ];
}

function financedUpfront(c, label) {
  const sd = c.upfrontSd || 0;
  const depositOnly = c.deposit;
  const working =
    sd > 0
      ? `${fmt(depositOnly)} deposit + ${fmt(sd)} stamp duty = ${fmt(depositOnly + sd)}`
      : `${fmt(depositOnly)} deposit (stamp duty financed)`;
  return {
    key: 'upfront',
    label,
    value: depositOnly + sd,
    sign: '+',
    working,
  };
}

function itemsLoan(c, ctx) {
  return [
    financedUpfront(c, 'Deposit + upfront stamp duty'),
    {
      key: 'payments',
      label: 'Loan repayments',
      value: c.payments,
      sign: '+',
      working: `monthly repayments over the hold period = ${fmt(c.payments)}`,
    },
    {
      key: 'running',
      label: 'Running costs',
      value: c.running,
      sign: '+',
      working: `${fmt(ctx.runPerYear)} / yr × ${ctx.holdYears} yrs = ${fmt(c.running)}`,
    },
    {
      key: 'opportunityCost',
      label: 'Opportunity cost (on upfront)',
      value: c.opportunityCost,
      sign: '+',
      working: `interest forgone on the upfront outlay = ${fmt(c.opportunityCost)}`,
    },
    {
      key: 'exitValue',
      label: 'Exit value (sale price)',
      value: c.exitValue,
      sign: '-',
      working: `resale at year ${ctx.holdYears} = ${fmt(c.exitValue)}`,
    },
  ];
}

function itemsDealer(c, ctx) {
  const items = [
    financedUpfront(c, 'Deposit + upfront stamp duty'),
    {
      key: 'payments',
      label: 'Finance repayments',
      value: c.payments,
      sign: '+',
      working: `monthly repayments over the hold period = ${fmt(c.payments)}`,
    },
    {
      key: 'running',
      label: 'Running costs',
      value: c.running,
      sign: '+',
      working: `${fmt(ctx.runPerYear)} / yr × ${ctx.holdYears} yrs = ${fmt(c.running)}`,
    },
  ];
  if (c.balloonOwing > 0) {
    items.push({
      key: 'balloonOwing',
      label: 'Balloon owing at term end',
      value: c.balloonOwing,
      sign: '+',
      working: `25% balloon payable at the end of the term = ${fmt(c.balloonOwing)}`,
    });
  }
  items.push({
    key: 'opportunityCost',
    label: 'Opportunity cost (on upfront)',
    value: c.opportunityCost,
    sign: '+',
    working: `interest forgone on the upfront outlay = ${fmt(c.opportunityCost)}`,
  });
  items.push({
    key: 'exitValue',
    label: 'Exit value (sale price)',
    value: c.exitValue,
    sign: '-',
    working: `resale at year ${ctx.holdYears} = ${fmt(c.exitValue)}`,
  });
  return items;
}

function itemsNovated(c, ctx, result) {
  const items = [
    {
      key: 'finance',
      label: 'Lease finance (pre-tax)',
      value: c.finance,
      sign: '+',
      working: c.purchaseGstCredit > 0
        ? `gross lease payments over the term on ${fmt(c.principal)} financed (${fmt(c.driveAway)} drive-away − ${fmt(c.purchaseGstCredit)} GST credit on purchase) = ${fmt(c.finance)}`
        : `gross lease payments over the term on ${fmt(c.principal)} financed = ${fmt(c.finance)}`,
    },
    {
      key: 'leaseRunning',
      label: 'Running costs (packaged, pre-tax)',
      value: c.leaseRunning,
      sign: '+',
      working: `${fmt(ctx.runPerYear)} / yr packaged over the lease term = ${fmt(c.leaseRunning)}`,
    },
    {
      key: 'taxSaving',
      label: 'Income tax saving',
      value: c.taxSaving,
      sign: '-',
      working: `${fmt(result.taxSavingMonthly)} / mo saved across the lease = ${fmt(c.taxSaving)}`,
    },
  ];
  if (!result.fbtExempt && c.fbt > 0) {
    items.push({
      key: 'fbt',
      label: 'FBT (ECM contribution)',
      value: c.fbt,
      sign: '+',
      working: `post-tax employee contribution over the term = ${fmt(c.fbt)}`,
    });
  }
  if (c.gstCredit > 0) {
    items.push({
      key: 'gstCredit',
      label: 'GST input credit',
      value: c.gstCredit,
      sign: '-',
      working: `GST credited on packaged running costs = ${fmt(c.gstCredit)}`,
    });
  }
  items.push({
    key: 'residual',
    label: 'Residual buyout',
    value: c.residual,
    sign: '+',
    working: `ATO residual payable at lease end = ${fmt(c.residual)}`,
  });
  if (c.postLeaseRunning > 0) {
    items.push({
      key: 'postLeaseRunning',
      label: 'Post-lease running costs',
      value: c.postLeaseRunning,
      sign: '+',
      working: `${fmt(ctx.runPerYear)} / yr after the lease ends = ${fmt(c.postLeaseRunning)}`,
    });
  }
  items.push({
    key: 'exitValue',
    label: 'Exit value (sale price)',
    value: c.exitValue,
    sign: '-',
    working: `resale at year ${ctx.holdYears} = ${fmt(c.exitValue)}`,
  });
  return items;
}

/**
 * Build LineItem[] for one QuickCompare result.
 * @param {object} result - a calcResults result with a `_components` object
 * @param {object} ctx - { exitValue, runPerYear, holdYears, stampDuty, state? }
 * @returns {LineItem[]} N+1 items; terminal item is the 'total' row.
 */
export function buildQuickCompareItems(result, ctx) {
  const c = result._components;
  if (!c) {
    throw new Error(
      `buildQuickCompareItems: result "${result.method}" has no _components`,
    );
  }
  let items;
  switch (result.method) {
    case 'cash':
      items = itemsCash(c, ctx);
      break;
    case 'loan':
      items = itemsLoan(c, ctx);
      break;
    case 'dealer':
      items = itemsDealer(c, ctx);
      break;
    case 'novated':
      items = itemsNovated(c, ctx, result);
      break;
    default:
      throw new Error(
        `buildQuickCompareItems: unknown method "${result.method}"`,
      );
  }
  items.push(totalItem(result));
  return items;
}
